import React, { useState, useMemo, useCallback } from 'react';
import { useStoreon } from 'storeon/react';
import { State } from 'store/types';
import { createElementKey } from 'utils';
import { analyticsUtils } from 'utils/analytics-utils';
import { LANDINGS_STORE_NAME } from 'modules/landings-info';
import { SERVICE_CATEGORIES_STORE_NAME } from 'modules/service-categories';
import {
  Button,
  CardLinkWithGAClickButton,
  CardMedia,
  MediaCard,
  MediaCardContent,
  Section,
  SectionSubTitle,
  Typography,
} from 'common/components';
import {
  CONSTRUCTION_HOUSES_PROJECTS_LIST,
  CONSTRUCTION_HOUSES_PROJECTS_PER_PAGE,
} from './construction-houses-projects-section-constants';
import { CONSTRUCTION_HOUSES_ELEMENT_BLOCK } from '../../construction-houses-constants';
import {
  constructionHousesProjectContent,
  constructionHousesProjectImage,
  constructionHousesProjectName,
  constructionHousesProjectsGrid,
  constructionHousesProjectsMoreBtnWrapper,
} from './construction-houses-projects-section-styles';
import { getFinalProjectsData } from './construction-houses-projects-section-utils';

type ConstructionHousesProjectsSectionProps = {
  sectionRef?: (el: HTMLElement | null) => void;
};

export const ConstructionHousesProjectsSection: React.FC<ConstructionHousesProjectsSectionProps> = ({
  sectionRef,
}) => {
  const { landings } = useStoreon<State>(LANDINGS_STORE_NAME);
  const { serviceCategories } = useStoreon<State>(SERVICE_CATEGORIES_STORE_NAME);

  const { landingsList } = landings;
  const { serviceCategoriesList } = serviceCategories;

  const finalFullProjectsList = useMemo(
    () => getFinalProjectsData(CONSTRUCTION_HOUSES_PROJECTS_LIST, landingsList, serviceCategoriesList),
    [landingsList, serviceCategoriesList]
  );

  const [projectsPerPage, setProjectsPerPage] = useState<number>(CONSTRUCTION_HOUSES_PROJECTS_PER_PAGE);
  const shownProjectsList = useMemo(() => finalFullProjectsList.slice(0, projectsPerPage), [
    finalFullProjectsList,
    projectsPerPage,
  ]);
  const isShowMoreBtnVisible = useMemo(() => projectsPerPage < finalFullProjectsList.length, [
    projectsPerPage,
    finalFullProjectsList,
  ]);

  const handleClickMoreBtn = useCallback(() => {
    setProjectsPerPage(projectsPerPage + CONSTRUCTION_HOUSES_PROJECTS_PER_PAGE);
    analyticsUtils.clickButtonEvent(CONSTRUCTION_HOUSES_ELEMENT_BLOCK, 'Показать ещё', 'Наши проекты');
  }, [projectsPerPage, setProjectsPerPage]);

  return (
    <Section ref={sectionRef}>
      <SectionSubTitle margin="m">Наши проекты</SectionSubTitle>
      <div className={constructionHousesProjectsGrid}>
        {shownProjectsList.map((project, index) => (
          <CardLinkWithGAClickButton
            elementBlock={CONSTRUCTION_HOUSES_ELEMENT_BLOCK}
            elementName={project.projectName}
            elementText="Наши проекты"
            title={project.projectName}
            href={project.projectUrl}
            key={createElementKey('project-card', index)}
            rel="noreferrer">
            <MediaCard>
              <CardMedia
                alt={project.projectName}
                className={constructionHousesProjectImage}
                loading="lazy"
                src={project.projectImage || ''}
                title={project.projectName}
                width="377"
                height="262"
              />
              <MediaCardContent className={constructionHousesProjectContent}>
                <Typography className={constructionHousesProjectName} as="h3" variant="h3">
                  {project.projectName.replace('на ', 'на\u00A0').replace('и ', 'и\u00A0')}
                </Typography>
              </MediaCardContent>
            </MediaCard>
          </CardLinkWithGAClickButton>
        ))}
      </div>
      {isShowMoreBtnVisible && (
        <div className={constructionHousesProjectsMoreBtnWrapper}>
          <Button onClick={handleClickMoreBtn} rounded>
            Показать еще
          </Button>
        </div>
      )}
    </Section>
  );
};
